import { FC, useEffect, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { ServiceNoReplyEmail } from '../../services/service-noreply-email';
import { TabPage } from '../../components/shared/PageWithTabs/TabPage';
import { TabsType } from '../../components/shared/PageWithTabs/PageWithTabs';
import Card from '../../components/shared/Card/card';
import BtButton from '../bt-button/bt-button';

type Props = { tabs: TabsType };

type Status = {
  isRunningOnThisServer?: boolean;
  // null = lock is free; the singleton lock document may still exist server-side
  currentLock?: { lockId: string; timestamp: number; age: number; isStale: boolean } | null;
  lastSyncTime?: string;
  emailCount?: number;
  // Outcome of the last cycle this server ran — the only place a run that
  // outlived the request deadline (answered 202) reports what it did.
  lastCycle?: {
    processed?: number;
    fetched?: number;
    added?: number;
    failed?: number;
    stopped?: boolean;
    error?: string;
    finishedAt?: string;
  } | null;
};

const StatusRow: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
    <span style={{ color: 'var(--gray-700)', fontWeight: 500 }}>{label}</span>
    <span style={{ color: 'var(--gray-900)' }}>{children}</span>
  </div>
);

export const NoReplyEmailTab: FC<Props> = ({ tabs }) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const result = await ServiceNoReplyEmail.getStatus();
      setStatus(result);
    } catch (error: any) {
      console.error('Error fetching status:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleProcessNow = async () => {
    setLoading(true);
    try {
      const result = await ServiceNoReplyEmail.processUnreadEmails();
      if (result.stillRunning) {
        NotificationManager.info(result.message);
      } else if (result.success) {
        const summary = `Processed ${result.processed} emails, ${result.added} added to conversations`;
        if (result.stopped) {
          // The cycle gave up its lock lease part-way; the rest of the mailbox
          // is untouched and the next poll picks it up. Failures within the part
          // it did reach still matter, so report both.
          const alsoFailed = result.failed > 0 ? `, ${result.failed} failed` : '';
          NotificationManager.warning(
            `${summary}${alsoFailed} — stopped early after losing the lock lease, ${result.fetched - result.processed} left for the next cycle`
          );
        } else if (result.failed > 0) {
          // A partial failure still answers 200 (some emails did land), so say so
          // here — otherwise the run reads as an unqualified success and the
          // failures are only visible in the server logs.
          NotificationManager.warning(`${summary}, ${result.failed} failed — see server logs`);
        } else {
          NotificationManager.success(summary);
        }
      } else {
        NotificationManager.error('Failed to process emails');
      }
      await fetchStatus();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        // Not a failure: the mailbox is being processed by a running cycle.
        NotificationManager.info(error.response.data?.message || 'A processing cycle is already running');
      } else {
        // Prefer the server's own diagnosis ("All 3 email(s) failed…") over
        // axios's generic "Request failed with status code 500".
        NotificationManager.error(
          error?.response?.data?.message || error?.message || 'Failed to process emails'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabPage tabs={tabs} title="Email Debug">
      <p style={{ color: 'var(--gray-600)', marginBottom: 24 }}>
        Manage the email processing service for conversation replies.
      </p>

      <Card title="Current Status">
        {status ? (
          <div style={{ marginTop: 16 }}>
            <StatusRow label="Running on this server:">
              <span style={{ color: status.isRunningOnThisServer ? '#10b981' : 'var(--gray-500)', fontWeight: status.isRunningOnThisServer ? 600 : 400 }}>
                {status.isRunningOnThisServer ? '✓ Yes' : '✗ No'}
              </span>
            </StatusRow>
            {status.currentLock && (
              <>
                <StatusRow label="Lock ID:">
                  <code style={{ fontSize: 12, background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{status.currentLock.lockId}</code>
                </StatusRow>
                <StatusRow label="Lock age:">
                  <span style={{ color: status.currentLock.isStale ? '#ef4444' : 'var(--gray-900)', fontWeight: status.currentLock.isStale ? 600 : 400 }}>
                    {Math.floor(status.currentLock.age / 1000)}s{status.currentLock.isStale ? ' — stale, holder may be dead' : ''}
                  </span>
                </StatusRow>
              </>
            )}
            {/* These two come from the answering instance's memory, not the DB —
                behind the load balancer another instance may have synced later. */}
            <StatusRow label="Last sync (this server):">
              {status.lastSyncTime ? new Date(status.lastSyncTime).toLocaleString() : 'Never'}
            </StatusRow>
            <StatusRow label="Cached emails (this server):">{status.emailCount}</StatusRow>
            {status.lastCycle && (
              <StatusRow label="Last cycle (this server):">
                {status.lastCycle.error
                  ? `failed: ${status.lastCycle.error}`
                  : `${status.lastCycle.added} added, ${status.lastCycle.failed} failed of ${status.lastCycle.processed} processed${status.lastCycle.stopped ? ' (stopped early)' : ''}`}
                {/* Undated, this row cannot be told apart from a much older run —
                    and it is the only channel a 202'd cycle reports through. */}
                {status.lastCycle.finishedAt && ` — ${new Date(status.lastCycle.finishedAt).toLocaleString()}`}
              </StatusRow>
            )}
          </div>
        ) : (
          <p style={{ marginTop: 16 }}>Loading status...</p>
        )}
      </Card>

      <Card title="Actions">
        <div style={{ marginTop: 16 }}>
          <BtButton onClick={handleProcessNow} disabled={loading}>
            Process Emails Now
          </BtButton>
          <p style={{ marginTop: 16, fontSize: 13, color: 'var(--gray-700)' }}>
            <strong>Process Emails Now:</strong> Manually triggers email processing once. Auto-refresh runs on all servers at startup; one server processes per cycle.
          </p>
        </div>
      </Card>
    </TabPage>
  );
};
